import React, { FC, PropsWithChildren, RefObject, useCallback } from 'react';
import {
  Modal as ChakraModal,
  ModalOverlay as ChakraModalOverlay,
  ModalContent as ChakraModalContent,
  ModalHeader as ChakraModalHeader,
  ModalFooter as ChakraModalFooter,
  ModalBody as ChakraModalBody,
  ModalCloseButton as ChakraModalCloseButton,
  Drawer,
  DrawerOverlay,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerContent,
  DrawerCloseButton,
  SlideDirection,
  Box,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
} from '@chakra-ui/react';
import { Button as CoreButton, ButtonVariant } from '../Button';
import { ModalSize, ModalType, ModalVariant } from './types';

export interface ModalProps extends PropsWithChildren {
  id?: string;
  isOpen: boolean | false;
  onClose?: () => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  size?: ModalSize;
  modalType: ModalType;
  showCloseButton?: boolean;
  drawerPlacement?: SlideDirection;
  variant?: ModalVariant;
  isCentered?: boolean;
  isDestructive?: boolean;
  closeLabel?: string;
  actionLabel?: string;
  actionOnClick?: () => void;
  className?: string;
  containerRef?: React.RefObject<HTMLElement | null>;
}

export const Modal: FC<ModalProps> = ({
  id,
  modalType = 'modal',
  header,
  footer,
  size = 'lg',
  showCloseButton = true,
  isOpen,
  onClose,
  drawerPlacement = 'left',
  variant,
  isCentered,
  children,
  isDestructive,
  closeLabel = 'Close',
  actionLabel,
  actionOnClick,
  className,
  containerRef = undefined,
}) => {
  /**
   * Methods
   */
  const handleOnClose = useCallback(() => {
    if (!onClose) {
      return;
    }
    onClose();
  }, [onClose]);
  const actionButtonRef = React.useRef<HTMLButtonElement>(null) as RefObject<HTMLButtonElement>;

  /**
   * DOM
   */
  if (modalType === 'drawer') {
    return (
      <Drawer variant={variant} id={id} isOpen={isOpen} onClose={handleOnClose} size={size} placement={drawerPlacement}>
        <DrawerOverlay />
        <DrawerContent>
          {!!header && (
            <Box>
              <DrawerHeader data-object-type="core-drawer-header">{header}</DrawerHeader>
            </Box>
          )}
          {!!showCloseButton && <DrawerCloseButton data-object-type="core-drawer-close-button" />}
          <DrawerBody data-object-type="core-drawer-body">{children}</DrawerBody>
          {!!footer && <DrawerFooter data-object-type="core-drawer-footer">{footer}</DrawerFooter>}
        </DrawerContent>
      </Drawer>
    );
  }
  if (modalType === 'alert') {
    return (
      <AlertDialog
        closeOnOverlayClick={false}
        isCentered={true}
        size={'md'}
        id={id}
        isOpen={isOpen}
        onClose={handleOnClose}
        leastDestructiveRef={actionButtonRef}
      >
        <AlertDialogOverlay />
        <AlertDialogContent>
          {!!header && <AlertDialogHeader data-testid="core-modal-header">{header}</AlertDialogHeader>}
          <AlertDialogBody data-testid="core-modal-body">{children}</AlertDialogBody>
          <AlertDialogFooter data-testid="core-modal-footer">
            <CoreButton variant={ButtonVariant.SOLID} colorScheme={'gray'} onClick={onClose} ref={actionButtonRef}>
              {closeLabel}
            </CoreButton>
            {!!actionLabel && (
              <CoreButton
                variant={ButtonVariant.SOLID}
                colorScheme={isDestructive ? 'error' : 'brand'}
                onClick={isDestructive ? actionOnClick : actionOnClick || onClose}
              >
                {actionLabel}
              </CoreButton>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }
  return (
    <>
      <ChakraModal
        variant={variant}
        id={id}
        isOpen={isOpen}
        onClose={handleOnClose}
        size={size}
        motionPreset={'none'}
        portalProps={{ containerRef }}
        {...(isCentered === true && { blockScrollOnMount: true, scrollBehavior: 'inside' })}
      >
        <ChakraModalOverlay />
        <ChakraModalContent className={className}>
          {!!header && <ChakraModalHeader data-testid="core-modal-header">{header}</ChakraModalHeader>}
          {showCloseButton && <ChakraModalCloseButton data-testid="core-drawer-modal-button" />}
          <ChakraModalBody data-testid="core-modal-body">{children}</ChakraModalBody>
          {!!footer && <ChakraModalFooter data-testid="core-modal-footer">{footer}</ChakraModalFooter>}
        </ChakraModalContent>
      </ChakraModal>
    </>
  );
};
